<template>
  <div class="pdv-shell">
    <page-loading
      v-if="initialLoading"
      message="Carregando PDV"
      subtitle="Preparando catálogo"
    />

    <template v-else>
      <!-- QR -->
      <div v-if="qrPaymentActive" class="pdv-qr-screen">
        <div class="pdv-qr-screen__header">
          <v-icon color="white" class="mr-2">mdi-qrcode-scan</v-icon>
          Aguardando pagamento BRPoints
        </div>
        <v-card class="pdv-qr-screen__card" elevation="4">
          <transaction-qr-code-panel
            :customer-name="selectedCustomer?.name || ''"
            :qrcode-image="qrcodeImage"
            :qrcode-text="qrcodeText"
            :status="transactionStatus"
          />
        </v-card>
      </div>

      <div v-else class="pdv-layout">
        <!-- Header -->
        <header class="pdv-topbar">
          <div class="pdv-topbar__left">
            <v-btn icon dark @click="$router.push('/sales')">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <div>
              <h1 class="pdv-topbar__title">Nova venda</h1>
              <p class="pdv-topbar__subtitle">Passo {{ currentStep }} de 3</p>
            </div>
          </div>
        </header>

        <!-- Stepper -->
        <nav class="pdv-stepper">
          <button
            v-for="(step, index) in steps"
            :key="step.key"
            type="button"
            class="pdv-stepper__item"
            :class="{
              'pdv-stepper__item--active': currentStep === index + 1,
              'pdv-stepper__item--done': currentStep > index + 1,
              'pdv-stepper__item--clickable': currentStep > index + 1,
            }"
            :disabled="currentStep <= index + 1"
            @click="goToStep(index + 1)"
          >
            <span class="pdv-stepper__num">
              <v-icon v-if="currentStep > index + 1" small color="white">mdi-check</v-icon>
              <span v-else>{{ index + 1 }}</span>
            </span>
            <span class="pdv-stepper__label">{{ step.label }}</span>
          </button>
        </nav>

        <!-- Step 1: Produtos -->
        <section v-show="currentStep === 1" class="pdv-step-panel">
          <p class="pdv-step-hint">
            Adicione produtos ao cupom ou informe um valor avulso. Produtos são opcionais.
          </p>

          <v-row v-if="!cart.length" dense class="mb-3">
            <v-col cols="12">
              <label class="pdv-field-label pdv-field-label--dark">Valor avulso (sem produto)</label>
              <v-text-field
                v-model="manualAmount"
                placeholder="R$ 0,00"
                outlined
                dense
                hide-details
                color="primary"
                v-money="money"
              />
            </v-col>
          </v-row>

          <v-text-field
            v-model="search"
            placeholder="Buscar produto, SKU ou código de barras"
            prepend-inner-icon="mdi-barcode-scan"
            outlined
            dense
            hide-details
            clearable
            color="primary"
            class="pdv-search br-form-field mb-3"
            @input="debouncedLoadCatalog"
          />

          <div class="pdv-categories mb-3">
            <button
              type="button"
              class="pdv-category"
              :class="{ 'pdv-category--active': !selectedCategoryId }"
              @click="selectCategory('')"
            >Todos</button>
            <button
              v-for="category in categories"
              :key="category.id"
              type="button"
              class="pdv-category"
              :class="{ 'pdv-category--active': selectedCategoryId === category.id }"
              @click="selectCategory(category.id)"
            >{{ category.name }}</button>
          </div>

          <div v-if="products.length || catalogLoading" class="pdv-product-grid mb-2">
            <button
              v-for="product in products"
              :key="product.id"
              type="button"
              class="pdv-product"
              :class="{ 'pdv-product--in-cart': isInCart(product.id) }"
              @click="addToCart(product)"
            >
              <div class="pdv-product__icon">
                <img
                  v-if="product.imageUrl"
                  :src="product.imageUrl"
                  :alt="product.name"
                  class="pdv-product__image"
                />
                <v-icon v-else size="28" color="primary">mdi-package-variant-closed</v-icon>
                <span v-if="cartQty(product.id)" class="pdv-product__badge">{{ cartQty(product.id) }}</span>
              </div>
              <p class="pdv-product__name">{{ product.name }}</p>
              <p class="pdv-product__price">{{ formatPrice(product.salePrice) }}</p>
            </button>
          </div>

          <div v-if="catalogTotal > catalogLimit" class="d-flex justify-center mb-4">
            <v-pagination
              v-model="catalogPage"
              :length="catalogPageCount"
              :total-visible="5"
              color="primary"
              @input="loadCatalog"
            />
          </div>

          <p v-else-if="!catalogLoading && !products.length && (search || selectedCategoryId)" class="pdv-step-hint mb-4">
            Nenhum produto encontrado.
          </p>

          <div v-if="cart.length" class="pdv-mini-cart mb-4">
            <div class="pdv-mini-cart__header">
              <span>Cupom ({{ cartItemCount }} {{ cartItemCount === 1 ? 'item' : 'itens' }})</span>
              <v-btn text x-small color="primary" @click="clearCart">Limpar</v-btn>
            </div>
            <div v-for="item in cart" :key="item.product.id" class="pdv-mini-cart__line">
              <div class="pdv-mini-cart__line-left">
                <div class="pdv-mini-cart__qty">
                  <button type="button" class="pdv-qty-btn pdv-qty-btn--light" @click="changeQty(item, -1)">
                    <v-icon x-small>mdi-minus</v-icon>
                  </button>
                  <span>{{ item.quantity }}</span>
                  <button type="button" class="pdv-qty-btn pdv-qty-btn--light" @click="changeQty(item, 1)">
                    <v-icon x-small>mdi-plus</v-icon>
                  </button>
                </div>
                <span class="pdv-mini-cart__name">{{ item.product.name }}</span>
              </div>
              <span>{{ formatPrice(Number(item.product.salePrice) * item.quantity) }}</span>
            </div>
          </div>

          <div class="pdv-step-total">
            <span>Total</span>
            <strong>{{ formatPrice(total) }}</strong>
          </div>
        </section>

        <!-- Step 2: Cliente -->
        <section v-show="currentStep === 2" class="pdv-step-panel pdv-step-panel--center">
          <v-icon size="56" color="primary" class="mb-4">mdi-account-circle</v-icon>
          <h2 class="pdv-step-title">Identifique o cliente</h2>
          <p class="pdv-step-hint mb-6">Digite o CPF. Os dados aparecem automaticamente se já estiver cadastrado.</p>

          <v-text-field
            v-model="customerDocument"
            label="CPF do cliente"
            placeholder="000.000.000-00"
            prepend-inner-icon="mdi-card-account-details"
            outlined
            color="primary"
            v-mask="'###.###.###-##'"
            :loading="customerLoading"
            class="pdv-cpf-field"
            @input="onCustomerDocumentInput"
          />

          <v-card v-if="selectedCustomer" class="pdv-customer-card mt-4" outlined>
            <v-card-text class="pdv-customer-card__body">
              <v-avatar color="primary" size="52" class="pdv-customer-card__avatar">
                <span class="white--text font-weight-bold">{{ customerInitials }}</span>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="pdv-customer-card__name">{{ selectedCustomer.name }}</div>
                <div class="pdv-customer-card__doc">{{ formatDocument(selectedCustomer.document) }}</div>
              </div>
              <v-btn icon small class="br-icon-action" @click="clearCustomer">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-text>
          </v-card>
        </section>

        <!-- Step 3: Pagamento -->
        <section v-show="currentStep === 3" class="pdv-step-panel">
          <h2 class="pdv-step-title mb-1">Pagamento</h2>
          <p class="pdv-step-hint mb-4">Escolha a forma de pagamento e confirme a venda.</p>

          <v-card outlined class="mb-4 pa-4 pdv-summary-card">
            <div class="pdv-summary-row"><span>Cliente</span><strong>{{ selectedCustomer?.name }}</strong></div>
            <div class="pdv-summary-row"><span>Itens</span><strong>{{ cartItemCount || 'Valor avulso' }}</strong></div>
            <div v-if="cashbackBaseAmount > 0" class="pdv-summary-row"><span>Cashback direto ({{ cashbackDisplayPercent }}%)</span><strong>{{ formatPrice(estimatedCashback) }}</strong></div>
            <v-divider class="my-3" />
            <div class="pdv-summary-total">
              <span>Total</span>
              <strong>{{ formatPrice(total) }}</strong>
            </div>
          </v-card>

          <v-card
            v-if="cashbackBaseAmount > 0 && cashbackBreakdown.length"
            outlined
            class="mb-4 pa-4 pdv-cashback-detail"
          >
            <p class="pdv-cashback-detail__title mb-1">
              <v-icon small color="primary" class="mr-1">mdi-information-outline</v-icon>
              Como calculamos o cashback direto
            </p>
            <p class="pdv-cashback-detail__intro mb-3">
              O cliente recebe de volta parte do valor pago com BRPoints. Veja o detalhamento:
            </p>
            <div
              v-for="(line, index) in cashbackBreakdown"
              :key="index"
              class="pdv-cashback-detail__line"
            >
              <div class="pdv-cashback-detail__line-info">
                <span class="pdv-cashback-detail__line-label">{{ line.label }}</span>
                <span class="pdv-cashback-detail__line-formula">{{ line.explanation }}</span>
              </div>
              <strong class="pdv-cashback-detail__line-amount">{{ formatPrice(line.amount) }}</strong>
            </div>
            <v-divider class="my-3" />
            <div class="pdv-cashback-detail__total">
              <span>Total de cashback para o cliente</span>
              <strong>{{ formatPrice(estimatedCashback) }}</strong>
            </div>
          </v-card>

          <v-card v-else-if="cashbackBaseAmount > 0 && !cashbackBreakdown.length" outlined class="mb-4 pa-4">
            <p class="mb-0 grey--text text--darken-1">
              Nenhum cashback nesta venda — não há percentual de cashback direto configurado.
            </p>
          </v-card>

          <v-card outlined class="mb-4 pa-4 pdv-payment-brpoints">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-qrcode</v-icon>
              <div>
                <p class="mb-0 font-weight-medium">Pagamento com BRPoints</p>
                <p class="mb-0 caption grey--text">
                  O cliente paga o total via QR Code BRPoints.
                </p>
              </div>
            </div>
          </v-card>
        </section>

        <!-- Navegação -->
        <footer class="pdv-nav">
          <v-btn
            v-if="currentStep > 1"
            outlined
            color="primary"
            class="pdv-nav__back"
            @click="prevStep"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Voltar
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="currentStep === 1"
            dark
            color="primary"
            :disabled="!canProceedStep1"
            @click="nextStep"
          >
            Continuar
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
          <template v-else-if="currentStep === 2">
            <v-btn
              dark
              color="primary"
              :disabled="!selectedCustomer"
              @click="nextStep"
            >
              Ir para pagamento
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </template>
          <template v-else>
            <v-btn
              dark
              color="primary"
              x-large
              class="pdv-pay-btn"
              :disabled="!canFinalize"
              :loading="finalizing"
              @click="finalizeSale"
            >
              <v-icon left>mdi-cash-register</v-icon>
              Confirmar venda
            </v-btn>
          </template>
        </footer>
      </div>

      <!-- Pré-registro -->
      <v-dialog v-model="openDialogNotClient" max-width="440" persistent>
        <v-card>
          <v-card-title class="pb-1">
            <v-icon left color="primary">mdi-account-plus</v-icon>
            Cliente não encontrado
          </v-card-title>
          <v-card-text>
            <p class="mb-4 grey--text text--darken-1">
              Informe o primeiro nome para cadastrar e continuar.
            </p>
            <v-text-field v-model="clientFirstName" label="Primeiro nome *" outlined dense hide-details />
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="cancelAutoRegister">Cancelar</v-btn>
            <v-btn
              dark
              color="primary"
              :disabled="!clientFirstName.trim() || autoRegistering"
              :loading="autoRegistering"
              @click="confirmAutoRegister"
            >Cadastrar e continuar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import TransactionQrCodePanel from "@/components/sales/TransactionQrCodePanel.vue";
import { pdvApi } from "@/services/pdv/pdvApi";
import { GetPartner } from "@/services/partners/GetPartner";
import { GetTransactionStatus } from "@/services/sales/GetTransactionStatus";
import { AutoPreRegister } from "@/services/clients/AutoPreRegister";
import { buildTransactionQRCodeText } from "@/utils/transactionQrcode";
import QRCode from "qrcode";
import { CatalogProductType, PdvCartItem, ProductCategoryType } from "@/types/pdv/Pdv";

export default defineComponent({
  components: { PageLoading, TransactionQrCodePanel },
  data() {
    return {
      initialLoading: true,
      currentStep: 1,
      steps: [
        { key: "products", label: "Produtos" },
        { key: "customer", label: "Cliente" },
        { key: "payment", label: "Pagamento" },
      ],
      search: "",
      categories: [] as ProductCategoryType[],
      products: [] as CatalogProductType[],
      catalogPage: 1,
      catalogTotal: 0,
      catalogLimit: 14,
      catalogLoading: false,
      selectedCategoryId: "",
      cart: [] as PdvCartItem[],
      customerDocument: "",
      customerLoading: false,
      openDialogNotClient: false,
      clientFirstName: "",
      autoRegistering: false,
      selectedCustomer: null as { id: string; name: string; document: string } | null,
      manualAmount: "R$ 0,00",
      instantCashbackPercent: 0,
      finalizing: false,
      qrPaymentActive: false,
      qrcodeImage: "",
      qrcodeText: "",
      transactionStatus: null as string | null,
      pendingSaleId: "",
      pendingTransactionId: "",
      pollTimer: null as ReturnType<typeof setInterval> | null,
      money: { decimal: ",", thousands: ".", prefix: "R$ ", precision: 2 },
      searchTimeout: null as ReturnType<typeof setTimeout> | null,
    };
  },
  computed: {
    catalogPageCount(): number {
      return Math.max(1, Math.ceil(this.catalogTotal / this.catalogLimit));
    },
    cartItemCount(): number {
      return this.cart.reduce((sum, item) => sum + item.quantity, 0);
    },
    customerInitials(): string {
      const name = this.selectedCustomer?.name || "";
      const parts = name.trim().split(/\s+/);
      if (!parts.length) return "?";
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    subtotal(): number {
      if (this.cart.length) {
        return this.cart.reduce(
          (sum, item) => sum + Number(item.product.salePrice) * item.quantity,
          0,
        );
      }
      return this.parseMoney(this.manualAmount);
    },
    total(): number {
      return this.subtotal;
    },
    cashbackBaseAmount(): number {
      return this.total;
    },
    estimatedCashback(): number {
      const base = this.cashbackBaseAmount;
      if (base <= 0) return 0;
      if (!this.cart.length) {
        return base * (this.instantCashbackPercent / 100);
      }
      const subtotal = this.subtotal;
      if (subtotal <= 0) return 0;
      return this.cart.reduce((sum, item) => {
        const percent = this.resolveProductCashbackPercent(item.product);
        const itemTotal = Number(item.product.salePrice) * item.quantity;
        const itemBrpointsShare = base * (itemTotal / subtotal);
        return sum + itemBrpointsShare * (percent / 100);
      }, 0);
    },
    cashbackDisplayPercent(): number {
      if (this.cashbackBaseAmount <= 0) return 0;
      if (!this.cart.length) return this.instantCashbackPercent;
      return Math.round((this.estimatedCashback / this.cashbackBaseAmount) * 1000) / 10;
    },
    cashbackBreakdown(): Array<{ label: string; explanation: string; amount: number }> {
      const base = this.cashbackBaseAmount;
      if (base <= 0) return [];

      if (!this.cart.length) {
        const percent = this.instantCashbackPercent;
        if (percent <= 0) return [];
        const amount = base * (percent / 100);
        return [{
          label: "Valor avulso",
          explanation: `${this.formatPrice(base)} × ${percent}% de cashback`,
          amount,
        }];
      }

      const subtotal = this.subtotal;
      const lines: Array<{ label: string; explanation: string; amount: number }> = [];

      for (const item of this.cart) {
        const percent = this.resolveProductCashbackPercent(item.product);
        const itemTotal = Number(item.product.salePrice) * item.quantity;
        const itemBrpointsShare = base * (itemTotal / subtotal);
        const amount = itemBrpointsShare * (percent / 100);
        const qtySuffix = item.quantity > 1 ? ` (${item.quantity} un.)` : "";
        const label = `${item.product.name}${qtySuffix}`;

        if (percent === 0) {
          lines.push({
            label,
            explanation: "Este produto não gera cashback direto",
            amount: 0,
          });
          continue;
        }

        lines.push({
          label,
          explanation: `${this.formatPrice(itemTotal)} × ${percent}% de cashback`,
          amount,
        });
      }

      return lines;
    },
    canProceedStep1(): boolean {
      return this.cart.length > 0 || this.parseMoney(this.manualAmount) > 0;
    },
    canFinalize(): boolean {
      return this.total > 0 && !!this.selectedCustomer;
    },
  },
  async mounted() {
    try {
      const partner = await new GetPartner().execute();
      this.instantCashbackPercent = Number(partner.partnerConfigs?.instantCashback || 0);
      const categoriesResult = await pdvApi.listCategories({ isActive: true, limit: 100 });
      this.categories = categoriesResult.data || [];
      await this.loadCatalog();
    } finally {
      this.initialLoading = false;
    }
  },
  methods: {
    resolveProductCashbackPercent(product: CatalogProductType): number {
      if (product.noInstantCashback) return 0;
      if (product.instantCashbackPercent != null) {
        return Number(product.instantCashbackPercent);
      }
      return this.instantCashbackPercent;
    },
    nextStep() {
      if (this.currentStep === 1 && !this.canProceedStep1) return;
      if (this.currentStep === 2 && !this.selectedCustomer) return;
      if (this.currentStep >= 3) return;
      this.currentStep += 1;
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep -= 1;
    },
    goToStep(step: number) {
      if (step < this.currentStep) {
        this.currentStep = step;
      }
    },
    parseMoney(value: string): number {
      if (!value) return 0;
      const normalized = value.replace("R$", "").replace(/\./g, "").replace(",", ".").trim();
      return Number(normalized) || 0;
    },
    formatPrice(value: number): string {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    formatDocument(doc: string): string {
      const digits = (doc || "").replace(/\D/g, "");
      if (digits.length !== 11) return doc;
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    },
    isInCart(productId: string): boolean {
      return this.cart.some((item) => item.product.id === productId);
    },
    cartQty(productId: string): number {
      return this.cart.find((i) => i.product.id === productId)?.quantity || 0;
    },
    selectCategory(id: string) {
      this.selectedCategoryId = id;
      this.catalogPage = 1;
      this.loadCatalog();
    },
    debouncedLoadCatalog() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.catalogPage = 1;
        this.loadCatalog();
      }, 300);
    },
    async loadCatalog() {
      this.catalogLoading = true;
      try {
        const result = await pdvApi.listCatalog({
          categoryId: this.selectedCategoryId || undefined,
          search: this.search || undefined,
          page: this.catalogPage,
          limit: this.catalogLimit,
        });
        this.products = result.data || [];
        this.catalogTotal = result.total || 0;
      } finally {
        this.catalogLoading = false;
      }
    },
    addToCart(product: CatalogProductType) {
      const existing = this.cart.find((item) => item.product.id === product.id);
      if (existing) {
        existing.quantity += 1;
        return;
      }
      this.cart.push({ product, quantity: 1 });
    },
    clearCart() {
      this.cart = [];
    },
    changeQty(item: PdvCartItem, delta: number) {
      const next = item.quantity + delta;
      if (next <= 0) {
        this.cart = this.cart.filter((c) => c.product.id !== item.product.id);
        return;
      }
      item.quantity = next;
    },
    onCustomerDocumentInput() {
      const doc = this.customerDocument.replace(/\D/g, "");
      if (this.selectedCustomer && this.selectedCustomer.document !== doc) {
        this.selectedCustomer = null;
      }
      if (doc.length === 11) this.lookupCustomer(doc);
    },
    async lookupCustomer(document?: string) {
      const doc = (document || this.customerDocument).replace(/\D/g, "");
      if (doc.length !== 11) return;
      this.customerLoading = true;
      try {
        const customers = await pdvApi.searchCustomers(doc);
        if (customers.length) {
          this.selectedCustomer = {
            id: customers[0].id,
            name: customers[0].name,
            document: customers[0].document.replace(/\D/g, ""),
          };
          return;
        }
        this.openDialogNotClient = true;
      } catch {
        this.$store.dispatch("showSnack", { message: "Erro ao buscar cliente", color: "error", timer: 3000 });
      } finally {
        this.customerLoading = false;
      }
    },
    cancelAutoRegister() {
      this.openDialogNotClient = false;
      this.clientFirstName = "";
    },
    async confirmAutoRegister() {
      if (!this.clientFirstName.trim()) return;
      this.autoRegistering = true;
      try {
        const customer = await new AutoPreRegister().execute(
          this.customerDocument,
          this.clientFirstName.trim(),
        );
        this.selectedCustomer = {
          id: customer.id || "",
          name: customer.name,
          document: customer.document.replace(/\D/g, ""),
        };
        this.openDialogNotClient = false;
        this.clientFirstName = "";
        this.$store.dispatch("showSnack", { message: "Cliente cadastrado", color: "success", timer: 3000 });
      } catch {
        const customers = await pdvApi.searchCustomers(this.customerDocument.replace(/\D/g, "")).catch(() => []);
        if (customers.length) {
          this.selectedCustomer = {
            id: customers[0].id,
            name: customers[0].name,
            document: customers[0].document.replace(/\D/g, ""),
          };
          this.openDialogNotClient = false;
          this.clientFirstName = "";
          return;
        }
        this.$store.dispatch("showSnack", { message: "Erro ao cadastrar cliente", color: "error", timer: 3000 });
      } finally {
        this.autoRegistering = false;
      }
    },
    clearCustomer() {
      this.selectedCustomer = null;
      this.customerDocument = "";
    },
    buildPayments() {
      return [{ paymentMethod: "BRPOINTS", amount: this.total }];
    },
    async finalizeSale() {
      if (!this.selectedCustomer || !this.canFinalize) return;
      this.finalizing = true;
      try {
        const payments = this.buildPayments();
        const payload: Record<string, unknown> = {
          customerId: this.selectedCustomer.id,
          payments,
          discountAmount: 0,
        };
        if (this.cart.length) {
          payload.items = this.cart.map((item) => ({
            productId: item.product.id,
            quantity: item.quantity,
          }));
        } else {
          payload.items = [];
          payload.manualAmount = this.parseMoney(this.manualAmount);
        }
        const result = await pdvApi.createSale(payload);
        const brpointsPayment = payments[0];
        if (result.transactionId && brpointsPayment) {
          const qrText = buildTransactionQRCodeText({
            transactionId: result.transactionId,
            value: brpointsPayment.amount,
            pagbank: result.partner.pagbank,
            partner: result.partner,
          });
          this.qrcodeText = qrText;
          this.qrcodeImage = await QRCode.toDataURL(qrText);
          this.transactionStatus = null;
          this.pendingSaleId = result.saleId;
          this.pendingTransactionId = result.transactionId;
          this.qrPaymentActive = true;
          this.startTransactionPolling();
        } else {
          this.$store.dispatch("showSnack", {
            message: `Venda #${result.saleNumber} finalizada`,
            color: "success",
            timer: 4000,
          });
          this.resetSale();
        }
      } catch (error: any) {
        this.$store.dispatch("showSnack", {
          message: error?.response?.data?.message || "Erro ao finalizar venda",
          color: "error",
          timer: 4000,
        });
      } finally {
        this.finalizing = false;
      }
    },
    startTransactionPolling() {
      this.stopTransactionPolling();
      const getStatus = new GetTransactionStatus();
      this.pollTimer = setInterval(async () => {
        if (!this.pendingTransactionId) return;
        try {
          const tx = await getStatus.execute(this.pendingTransactionId);
          if (tx?.status === "CONFIRMED") await this.handleBrpointsConfirmed();
        } catch { /* keep polling */ }
      }, 5000);
    },
    stopTransactionPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async handleBrpointsConfirmed() {
      if (!this.pendingSaleId) return;
      await pdvApi.confirmBrpoints(this.pendingSaleId);
      this.transactionStatus = "CONFIRMED";
      this.stopTransactionPolling();
      this.$store.dispatch("showSnack", { message: "Pagamento confirmado", color: "success", timer: 4000 });
      setTimeout(() => {
        this.resetQrPayment();
        this.resetSale();
      }, 2000);
    },
    resetQrPayment() {
      this.qrPaymentActive = false;
      this.qrcodeImage = "";
      this.qrcodeText = "";
      this.transactionStatus = null;
      this.pendingSaleId = "";
      this.pendingTransactionId = "";
      this.stopTransactionPolling();
    },
    resetSale() {
      this.cart = [];
      this.selectedCustomer = null;
      this.customerDocument = "";
      this.manualAmount = "R$ 0,00";
      this.currentStep = 1;
      this.loadCatalog();
    },
  },
  beforeDestroy() {
    this.stopTransactionPolling();
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

$brand: $br-brand;
$dark: $br-ink;
$surface: $br-bg;
$muted: $br-muted;
$border: $br-border;
$radius: $br-radius-md;

.pdv-shell {
  margin: -8px -4px 0;
  min-height: calc(100vh - 120px);

  ::v-deep .v-text-field--outlined fieldset {
    border-color: $border;
  }

  ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
    border-color: $brand !important;
  }

  ::v-deep .v-input--is-label-active .v-label,
  ::v-deep .v-input--is-focused .v-label {
    color: $brand !important;
  }
}

.pdv-layout {
  background: $br-bg-elevated;
  border-radius: $br-radius-xl;
  overflow: hidden;
  box-shadow: $br-shadow-lg;
  border: 1px solid $br-border-soft;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 140px);
}

.pdv-topbar {
  display: flex;
  align-items: center;
  background:
    radial-gradient(circle at 90% 20%, rgba(255, 255, 255, 0.1), transparent 40%),
    linear-gradient(135deg, $dark 0%, $br-ink-soft 55%, #2a3058 100%);
  color: #fff;
  padding: 14px 18px;
}

.pdv-topbar__left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pdv-topbar__title {
  font-size: 1.15rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.2px;
}

.pdv-topbar__subtitle {
  font-size: 0.75rem;
  margin: 0;
  opacity: 0.7;
  font-weight: 600;
}

.pdv-stepper {
  display: flex;
  background: linear-gradient(180deg, $br-bg-elevated, $br-bg-subtle);
  padding: 14px 16px;
  gap: 8px;
  border-bottom: 1px solid $border;
}

.pdv-stepper__item {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: 0.42;
  transition: opacity 0.2s, background 0.15s, transform 0.15s;
  border: none;
  background: transparent;
  padding: 8px 10px;
  border-radius: $br-radius-md;
  cursor: default;
  text-align: left;

  &--active,
  &--done {
    opacity: 1;
  }

  &--active {
    background: rgba($brand, 0.07);
  }

  &--clickable {
    cursor: pointer;

    &:hover {
      background: rgba($brand, 0.08);
      transform: translateY(-1px);
    }
  }

  &:disabled {
    cursor: default;
  }
}

.pdv-stepper__num {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #d5dae8;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);

  .pdv-stepper__item--active & {
    background: linear-gradient(135deg, $br-brand-light, $brand);
    box-shadow: 0 4px 12px rgba(229, 24, 61, 0.3);
  }

  .pdv-stepper__item--done & {
    background: $br-success;
  }
}

.pdv-stepper__label {
  font-size: 0.8rem;
  font-weight: 700;
  color: $br-ink-soft;

  @media (max-width: 600px) {
    font-size: 0.7rem;
  }
}

.pdv-step-panel {
  flex: 1;
  padding: 20px 16px 8px;
  overflow-y: auto;
  background: $surface;

  &--center {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 420px;
    margin: 0 auto;
    padding-top: 40px;
  }
}

.pdv-step-title {
  font-size: 1.3rem;
  font-weight: 800;
  color: $br-ink-soft;
  margin: 0;
  letter-spacing: -0.2px;
}

.pdv-step-hint {
  font-size: 0.85rem;
  color: $muted;
  margin: 0 0 16px;
  font-weight: 500;
}

.pdv-step-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: linear-gradient(135deg, $br-brand-soft, #fff);
  border: 1px solid rgba(229, 24, 61, 0.12);
  border-radius: $br-radius-md;
  font-size: 1rem;
  font-weight: 700;
  margin-top: 12px;
  box-shadow: $br-shadow-sm;

  strong {
    color: $brand;
    font-size: 1.25rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
}

.pdv-cpf-field {
  width: 100%;
  max-width: 320px;
}

.pdv-customer-card {
  width: 100%;
  max-width: 380px;
  border-radius: $br-radius-lg !important;
  border-color: rgba(229, 24, 61, 0.14) !important;
  background: linear-gradient(135deg, $br-brand-soft, $br-bg-elevated) !important;
  box-shadow: $br-shadow-sm !important;
}

.pdv-customer-card__body {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 16px !important;
}

.pdv-customer-card__avatar {
  margin-right: 12px;
  box-shadow: 0 4px 12px rgba(229, 24, 61, 0.25);
}

.pdv-customer-card__name {
  font-weight: 800;
  color: $br-ink-soft;
  line-height: 1.25;
}

.pdv-customer-card__doc {
  font-size: $br-font-size-sm;
  color: $br-muted;
  font-weight: 600;
  margin-top: 2px;
}

.pdv-summary-card {
  border-radius: $br-radius-lg !important;
  border-color: $br-border-soft !important;
  box-shadow: $br-shadow-sm !important;
  background: linear-gradient(180deg, $br-bg-elevated, $br-bg-subtle) !important;
}

.pdv-search ::v-deep .v-input__slot {
  background: #fff !important;
  border-radius: $br-radius-md !important;
  box-shadow: $br-shadow-sm;
}

.pdv-categories {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  padding-bottom: 2px;
  &::-webkit-scrollbar { display: none; }
}

.pdv-category {
  flex-shrink: 0;
  border: 1px solid $border;
  background: $br-bg-elevated;
  color: $muted;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 8px 16px;
  border-radius: $br-radius-pill;
  cursor: pointer;
  box-shadow: $br-shadow-sm;
  transition: transform $br-transition-fast, background $br-transition-fast,
    color $br-transition-fast, border-color $br-transition-fast, box-shadow $br-transition-fast;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba($brand, 0.3);
  }

  &--active {
    background: linear-gradient(135deg, $br-brand-light, $brand);
    color: #fff;
    border-color: $brand;
    box-shadow: 0 6px 16px rgba(229, 24, 61, 0.25);
  }
}

.pdv-product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 12px;
}

.pdv-product {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: $br-bg-elevated;
  border: 1.5px solid $br-border-soft;
  border-radius: $br-radius-lg;
  padding: 14px 10px 12px;
  cursor: pointer;
  transition: transform $br-transition-base, box-shadow $br-transition-base,
    border-color $br-transition-base, background $br-transition-base;
  box-shadow: $br-shadow-sm;

  &:hover:not(:disabled) {
    border-color: rgba($brand, 0.35);
    transform: translateY(-3px);
    box-shadow: $br-shadow-md;
  }

  &--in-cart {
    border-color: $brand;
    background: linear-gradient(180deg, rgba($brand, 0.06), $br-bg-elevated);
    box-shadow: 0 8px 18px rgba(229, 24, 61, 0.12);
  }
}

.pdv-cashback-detail {
  border-radius: $br-radius-lg !important;
  border-color: rgba(229, 24, 61, 0.12) !important;
  background: linear-gradient(180deg, $br-brand-soft, $br-bg-elevated) !important;
}

.pdv-cashback-detail__title {
  font-size: 0.95rem;
  font-weight: 800;
  color: $br-ink-soft;
  display: flex;
  align-items: center;
}

.pdv-cashback-detail__intro,
.pdv-cashback-detail__note {
  font-size: 0.85rem;
  color: $muted;
  line-height: 1.45;
}

.pdv-cashback-detail__line {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid $br-border-soft;

  &:last-of-type {
    border-bottom: none;
  }
}

.pdv-cashback-detail__line-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.pdv-cashback-detail__line-label {
  font-size: 0.875rem;
  font-weight: 700;
  color: $br-ink-soft;
}

.pdv-cashback-detail__line-formula {
  font-size: 0.8rem;
  color: $br-muted-light;
}

.pdv-cashback-detail__line-amount {
  font-size: 0.9rem;
  color: $brand;
  white-space: nowrap;
  font-weight: 800;
}

.pdv-cashback-detail__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.95rem;
  font-weight: 700;

  strong {
    color: $brand;
    font-size: 1.1rem;
    font-weight: 800;
  }
}

.pdv-product__icon {
  position: relative;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, $br-brand-soft, #fff);
  border-radius: 14px;
  margin-bottom: 10px;
  box-shadow: inset 0 0 0 1px rgba(229, 24, 61, 0.08);
}

.pdv-product__image {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 14px;
}

.pdv-product__badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: linear-gradient(135deg, $br-brand-light, $brand);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 800;
  min-width: 20px;
  height: 20px;
  line-height: 20px;
  border-radius: 10px;
  box-shadow: 0 3px 8px rgba(229, 24, 61, 0.35);
}

.pdv-product__name {
  font-size: 0.8rem;
  font-weight: 700;
  margin: 0 0 4px;
  line-height: 1.25;
  color: $br-ink-soft;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pdv-product__price {
  font-size: 1rem;
  font-weight: 800;
  color: $brand;
  margin: 0;
  font-variant-numeric: tabular-nums;
}

.pdv-mini-cart {
  background: $br-bg-elevated;
  border-radius: $br-radius-lg;
  padding: 14px;
  border: 1px solid $br-border-soft;
  border-left: 4px solid $brand;
  box-shadow: $br-shadow-sm;
}

.pdv-mini-cart__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 800;
  font-size: 0.88rem;
  margin-bottom: 10px;
  color: $br-ink-soft;
}

.pdv-mini-cart__line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.84rem;
  font-weight: 600;
  padding: 8px 0;
  border-bottom: 1px solid $br-border-soft;
  color: $br-ink-soft;

  &:last-child { border: none; }
}

.pdv-mini-cart__line-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.pdv-mini-cart__name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pdv-mini-cart__qty {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.pdv-qty-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid $br-border-soft;
  border-radius: 8px;
  background: $br-bg-elevated;
  cursor: pointer;
  transition: background $br-transition-fast, border-color $br-transition-fast,
    color $br-transition-fast;

  &:hover {
    background: $br-brand-soft;
    border-color: rgba($brand, 0.3);
    color: $brand;
  }

  &--light {
    border-color: $border;
  }
}

.pdv-field-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: $muted;
  margin-bottom: 6px;
}

.pdv-summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  margin-bottom: 8px;
  color: $muted;
  font-weight: 600;

  strong { color: $br-ink-soft; font-weight: 800; }
}

.pdv-summary-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 800;

  strong {
    color: $brand;
    font-size: 1.45rem;
    font-variant-numeric: tabular-nums;
  }
}

.pdv-payment-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.pdv-payment-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 18px 10px;
  border: 1.5px solid $br-border-soft;
  border-radius: $br-radius-lg;
  background: $br-bg-elevated;
  cursor: pointer;
  font-size: 0.84rem;
  font-weight: 700;
  color: $muted;
  transition: transform $br-transition-base, box-shadow $br-transition-base,
    border-color $br-transition-base, background $br-transition-base, color $br-transition-base;
  box-shadow: $br-shadow-sm;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba($brand, 0.28);
  }

  &--active {
    border-color: $brand;
    background: linear-gradient(180deg, rgba($brand, 0.08), $br-bg-elevated);
    color: $brand;
    box-shadow: 0 8px 18px rgba(229, 24, 61, 0.14);
  }
}

.pdv-nav {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border-top: 1px solid $border;
  box-shadow: 0 -8px 20px rgba(23, 29, 52, 0.05);
}

.pdv-nav__back {
  flex-shrink: 0;
}

.pdv-pay-btn {
  font-weight: 800 !important;
  border-radius: $br-radius-md !important;
  min-width: 200px;
}

.pdv-qr-screen {
  max-width: 480px;
  margin: 0 auto;
}

.pdv-qr-screen__header {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $dark, $br-ink-soft);
  color: #fff;
  padding: 16px;
  border-radius: $br-radius-lg $br-radius-lg 0 0;
  font-weight: 700;
}

.pdv-qr-screen__card {
  border-radius: 0 0 $br-radius-lg $br-radius-lg !important;
  padding: 16px;
  box-shadow: $br-shadow-md;
}
</style>
